import QRCode from 'react-qr-code';
import { useCallback, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadString,
} from 'firebase/storage';
import { getDatabase, push, ref as dbRef, set } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast, Toaster } from 'sonner';
import GoldScreenLayout from './GoldScreenLayout';
import GoldProcessingPage from './GoldProcessingPage';
import theme from './goldTheme.module.scss';
import styles from './GoldResultPage.module.scss';
import { clampVwMax, pxH, pxW } from './figmaScale';
import { FIGMA_SPEC } from './figmaSpec';
import { GOLD_ENABLE_FIREBASE_UPLOAD } from '../../config/gold';
import right from '../../images/gold/right.png';
import brandDark from '../../images/gold/brand_dark.svg';

const R = FIGMA_SPEC.result;
const FRAME = FIGMA_SPEC.camera.frame;

function GoldResultPage({ updateState, mergeData, generating, mergeError }) {
  const [email, setEmail] = useState('');
  const [qrLink, setQrLink] = useState('');
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const captureStartedRef = useRef(false);

  const buildLocalComposite = useCallback(async () => {
    if (captureStartedRef.current) return;
    const finalRender = document.getElementById('goldFinalRender');
    if (!finalRender || !mergeData?.data) return;

    captureStartedRef.current = true;
    try {
      await new Promise((r) => setTimeout(r, 400));
      const images = finalRender.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) resolve();
              else {
                img.onload = resolve;
                img.onerror = resolve;
              }
            })
        )
      );

      const canvas = await html2canvas(finalRender, {
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      setLocalPreviewUrl(dataUrl);

      if (GOLD_ENABLE_FIREBASE_UPLOAD) {
        const storage = getStorage();
        const database = getDatabase();
        const storageRef = ref(storage, `merged_gold/${Date.now()}.png`);
        await uploadString(storageRef, dataUrl, 'data_url');
        const downloadURL = await getDownloadURL(storageRef);
        setQrLink(downloadURL);
        const postListRef = dbRef(database, 'imagesGold');
        const newPostRef = push(postListRef);
        await set(newPostRef, { timestamp: Date.now(), url: downloadURL });
      }
    } catch (error) {
      console.error('Composite capture failed:', error);
      captureStartedRef.current = false;
    }
  }, [mergeData?.data]);

  useEffect(() => {
    captureStartedRef.current = false;
    setLocalPreviewUrl('');
    if (!GOLD_ENABLE_FIREBASE_UPLOAD) setQrLink('');
  }, [mergeData?.data]);

  const handleApiImageLoad = () => buildLocalComposite();

  const handleDevDownload = () => {
    const url = localPreviewUrl || mergeData?.data;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `golden-card-${Date.now()}.png`;
    a.click();
  };

  const isEmailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSendEmail = async () => {
    if (!email) {
      toast.error('Enter email!');
      return;
    }
    if (!isEmailValid(email)) {
      toast.error('Invalid email!');
      return;
    }
    if (!GOLD_ENABLE_FIREBASE_UPLOAD) {
      toast.info('Email send is disabled — use Download preview.');
      return;
    }
    if (!qrLink) {
      toast.error('Please wait for your image to finish generating.');
      return;
    }
    if (!sendEmail) {
      setLoading(true);
      const sendURLEmail = httpsCallable(getFunctions(), 'sendURLEmail');
      sendURLEmail({
        email,
        url: qrLink,
        subject: 'Your World Gold Council Golden Card is Ready!',
        emailV2: true,
      })
        .then(() => {
          setSendEmail(true);
          toast.success('Email with downloadable link sent!');
          setLoading(false);
        })
        .catch(() => {
          toast.error('An error occurred while sending your email.');
          setLoading(false);
        });
    } else {
      toast.success('Email already sent — check your inbox or spam.');
    }
  };

  const qrPx = Math.round(
    Math.min(160, (160 / 1080) * (typeof window !== 'undefined' ? window.innerWidth : 1080))
  );

  if (generating) return <GoldProcessingPage />;

  if (mergeError?.message) {
    return (
      <GoldScreenLayout showLogo={false}>
        <div style={{ padding: pxW(80), fontSize: pxH(32) }}>
          <p className={theme.bodyText}>Something went wrong: {mergeError.message}</p>
          <button
            type="button"
            className={theme.startBtn}
            style={{ marginTop: pxH(40), width: pxW(400), height: pxH(100), fontSize: pxH(32) }}
            onClick={() => updateState(0)}
          >
            Try Again
          </button>
        </div>
      </GoldScreenLayout>
    );
  }

  return (
    <GoldScreenLayout showLogo={false}>
      <div className={styles.resultSlide147}>
        <header
          className={styles.resultHeader}
          style={{ paddingTop: pxH(R.title.top), maxWidth: pxW(R.title.w) }}
        >
          <h1
            className={styles.textYourGoldenCard}
            style={{
              fontSize: clampVwMax(R.title.fontSize),
              lineHeight: R.title.lineHeight,
            }}
          >
            <span className={styles.titleLine}>Your Golden</span>
            <span className={styles.titleLine}>Card</span>
          </h1>
        </header>

        <div className={styles.cardWrap}>
          <div
            id="goldFinalRender"
            className={`${styles.frame427319946} ${styles.boothFrame}`}
            style={{
              aspectRatio: `${FIGMA_SPEC.camera.frame.w} / ${FIGMA_SPEC.camera.frame.h}`,
            }}
          >
            {mergeData?.data ? (
              <img
                src={mergeData.data}
                alt=""
                className={styles.apiImage}
                onLoad={handleApiImageLoad}
              />
            ) : null}
            <div
              className={styles.group1244831447}
              style={{
                left: `${(R.cardLogo.left / FRAME.w) * 100}%`,
                top: `${(R.cardLogo.top / FRAME.h) * 100}%`,
                width: `${(R.cardLogo.w / FRAME.w) * 100}%`,
                aspectRatio: `${R.cardLogo.w} / ${R.cardLogo.h}`,
              }}
            >
              <img
                src={brandDark}
                alt="World Gold Council"
                className={styles.image290Card}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>

        <div className={styles.resultFooter}>
          <div
            className={styles.sharePanel}
            style={{ gap: clampVwMax(R.sharePanelGap) }}
          >
            <div className={styles.qrColumn}>
              <p
                className={styles.scanLabel}
                style={{ fontSize: clampVwMax(R.scanLabel.fontSize) }}
              >
                Scan to Download
              </p>
              <div
                className={styles.rectangle22864}
                style={{
                  width: clampVwMax(R.qrBox.w),
                  height: clampVwMax(R.qrBox.h),
                  padding: clampVwMax(10),
                }}
              >
                {qrLink ? (
                  <QRCode value={qrLink} size={qrPx} />
                ) : GOLD_ENABLE_FIREBASE_UPLOAD ? (
                  <span className={styles.devQrNote} style={{ fontSize: pxH(14) }}>
                    Preparing QR...
                  </span>
                ) : (
                  <div>
                    <span className={styles.devQrNote} style={{ fontSize: pxH(16) }}>
                      QR unavailable
                    </span>
                    {localPreviewUrl ? (
                      <button
                        type="button"
                        className={styles.devDownloadBtn}
                        onClick={handleDevDownload}
                      >
                        Download preview
                      </button>
                    ) : (
                      <span className={styles.devQrNote} style={{ fontSize: pxH(14) }}>
                        Preparing…
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              className={styles.line19}
              style={{ height: clampVwMax(R.divider.h), alignSelf: 'stretch' }}
            />

            <div className={styles.emailColumn}>
              <p
                className={styles.shareLabel}
                style={{ fontSize: clampVwMax(R.shareLabel.fontSize) }}
              >
                Share via email
              </p>
              <div
                className={styles.frame427319944}
                style={{
                  width: '100%',
                  maxWidth: clampVwMax(R.emailField.w),
                  height: clampVwMax(R.emailField.h),
                  padding: `${clampVwMax(R.emailField.padY)} ${clampVwMax(R.emailField.padX)}`,
                }}
              >
                <div className={styles.frame427319945}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="abc@gmail.com"
                    className={styles.emailInput}
                    style={{ fontSize: clampVwMax(R.emailFont) }}
                  />
                </div>
              </div>
              <button
                type="button"
                className={styles.sendBtn}
                disabled={loading || !email || sendEmail}
                aria-busy={loading}
                style={{
                  height: clampVwMax(R.sendBtn.h),
                  fontSize: clampVwMax(R.emailFont),
                }}
                onClick={handleSendEmail}
              >
                {loading ? 'Sending...' : sendEmail ? 'Sent' : 'Send'}
              </button>
            </div>
          </div>

          <button
            type="button"
            className={styles.homeBtn}
            style={{
              marginTop: clampVwMax(10),
              width: clampVwMax(R.homeBtn.w),
              height: clampVwMax(R.homeBtn.h),
              fontSize: clampVwMax(R.emailFont),
            }}
            onClick={() => updateState(-1)}
          >
            Home
            <span
              className={styles.arrowRightAlt}
              style={{
                width: clampVwMax(R.homeArrow.w),
                height: clampVwMax(R.homeArrow.h),
              }}
            >
              <img src={right} alt="" style={{ width: '55%', height: '55%' }} />
            </span>
          </button>
        </div>
        <Toaster />
      </div>
    </GoldScreenLayout>
  );
}

export default GoldResultPage;
